/*
 *  -----------------------------------------------------------------------
 *    <copyright file="add-report-template-dialog/index.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2023 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { GetPowerBIReportTemplate, OpraReportTemplate } from '@interfaces/report-templates.interface';
import { DateTimePickerChangeEvent } from '@progress/kendo-react-dateinputs';
import { DropDownListChangeEvent } from '@progress/kendo-react-dropdowns';
import { Checkbox } from '@progress/kendo-react-inputs';
import cn from 'clsx';
import { isNumber, isString } from 'lodash';
import moment from 'moment';
import React, { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { ModuleSpecificCheckboxesState } from '@components/admin/add-custom-report-template-dialog/form-fields';
import {
    AddReportTemplateDialogProps,
    ReportTemplateFormInputs,
} from '@components/admin/add-report-template-dialog/add-report-template-dialog.interface';
import Button from '@components/core/buttons';
import CharactersRemaining from '@components/core/characters-remaining';
import DateTimePicker from '@components/core/date-time-picker';
import { CustomDialog, DialogActions, DialogTitle, DialogContent } from '@components/core/dialog';
import Loading from '@components/core/loading';
import ModalUpdatedInfo from '@components/core/modal-updated-info';
import SelectWithCategories from '@components/core/select-with-categories';
import TextField from '@components/core/text-field';
import {
    useGetPowerBIModuleTypes,
    useGetPowerBIReportFormatTypes,
    useGetReportTemplateNames,
    useUpsertPowerBIReportTemplate,
} from 'apis/bff-api/report-templates';
import { useMap, useTranslation } from '@hooks/index';
import Constants from '@utils/constants';
import { javascriptDateWithTimeFromUTC, unixDateFromUtc } from '@utils/convertor';
import NotificationService from '@utils/notification';
import { validateDate, validateFieldLength } from '@utils/validation';

import './styles.scss';

const AddReportTemplateDialog = ({
    currentReportTemplate,
    onClose,
    powerBIReportTemplatesData,
    confirmActionFn,
    className,
}: AddReportTemplateDialogProps) => {
    const { t } = useTranslation();
    const { trigger: upsertReportTemplate, isMutating } = useUpsertPowerBIReportTemplate();
    const { data: moduleTypesData, isLoading: isLoadingModuleTypes } = useGetPowerBIModuleTypes();
    const { data: formatTypesData, isLoading: isLoadingFormatTypes } = useGetPowerBIReportFormatTypes();
    const { data: reportTemplatesNamesData, isLoading: isLoadingReportTemplatesNames } = useGetReportTemplateNames();
    const reportTemplatesNames = reportTemplatesNamesData || [];
    const isLoadingData = isLoadingModuleTypes || isLoadingFormatTypes || isLoadingReportTemplatesNames;

    const convertDataToSelectOptions = <T extends { id?: string | number; name?: string | null; inUse?: boolean }>(
        data?: T[],
    ) =>
        data
            ?.filter((item) => !!item?.id && !!item?.name && !item?.inUse)
            ?.map((item) => ({
                id: item.id as string,
                text: item.name as string,
                active: true,
            }))
            ?.sort((a, b) => a.text.localeCompare(b.text)) ?? [];

    const getCurrentPBITemplateObject = () =>
        powerBIReportTemplatesData
            .filter((tmp: GetPowerBIReportTemplate) => tmp.id === currentReportTemplate?.pbiReportId)
            .map((tmp: GetPowerBIReportTemplate) => ({
                id: tmp?.id,
                text: tmp?.name,
                active: true,
            }));

    const formatTypeOptions = [
        { id: '', text: t('admin:selectValue'), active: true },
        ...convertDataToSelectOptions(formatTypesData),
    ];

    const moduleTypeOptions = [
        { id: '', text: t('admin:selectValue'), active: true },
        ...convertDataToSelectOptions(moduleTypesData),
    ];

    const powerBITemplateOptions = [
        { id: '', text: t('admin:selectValue'), active: true },
        ...(currentReportTemplate?.pbiReportId ? getCurrentPBITemplateObject() : []), // get pbiReport element for current pbiReportId select
        ...convertDataToSelectOptions(powerBIReportTemplatesData),
    ];

    const {
        control,
        handleSubmit,
        watch,
        getValues,
        setValue,
        formState: { isValid, isDirty, errors },
    } = useForm<ReportTemplateFormInputs>({
        mode: 'all',
        defaultValues: currentReportTemplate
            ? {
                  templateName: currentReportTemplate.templateName ?? '',
                  description: currentReportTemplate.description ?? '',
                  module: currentReportTemplate.module ?? '',
                  format: currentReportTemplate.format ?? '',
                  status: currentReportTemplate.status ?? '',
                  retirementDate: currentReportTemplate.retirementDate ?? null,
                  pbiReportId: currentReportTemplate.pbiReportId ?? '',
                  includeSignatures: currentReportTemplate.includeSignatures,
                  includeNameAndVersion: currentReportTemplate.includeNameAndVersion,
                  includeReviews: currentReportTemplate.includeReviews,
                  includeNotApplicableRisks: currentReportTemplate.includeNotApplicableRisks,
                  includeInternalRisks: currentReportTemplate.includeInternalRisks,
                  includeComparisonDataset: currentReportTemplate.includeComparisonDataset,
              }
            : {
                  templateName: '',
                  description: '',
                  module: '',
                  format: '',
                  status: 1,
                  retirementDate: null,
                  pbiReportId: '',
                  includeSignatures: false,
                  includeNameAndVersion: false,
                  includeReviews: false,
                  includeNotApplicableRisks: false,
                  includeInternalRisks: false,
                  includeComparisonDataset: false,
              },
    });

    const moduleSpecificCheckboxesState = useMap<OpraReportTemplate['module'], ModuleSpecificCheckboxesState>();

    const moduleFieldValue = watch('module');
    const statusFieldValue = watch('status');
    const retirementDateFieldValue = watch('retirementDate');

    const isPublishedStatus = statusFieldValue === Constants.Reporting.ReportTemplateStatus.published;
    const isRetiredStatus = statusFieldValue === Constants.Reporting.ReportTemplateStatus.retired;
    const isInputsDisabled = isPublishedStatus || isRetiredStatus;
    const hasErrorInFormatOrModule = !!errors.format?.message || !!errors.module?.message;
    const checkDateWithinLastHour = (lastAttemptedPublish: string | null | undefined) => {
        if (!lastAttemptedPublish) {
            return false;
        }

        const date = new Date();

        const dateToCompare = Date.UTC(
            date.getUTCFullYear(),
            date.getUTCMonth(),
            date.getUTCDate(),
            date.getUTCHours(),
            date.getUTCMinutes(),
            date.getUTCSeconds(),
            date.getUTCMilliseconds(),
        );

        const hour = 1000 * 60 * 60;
        return dateToCompare - hour < Date.parse(lastAttemptedPublish);
    };

    const dateWithinLastHour = checkDateWithinLastHour(currentReportTemplate?.lastAttemptedPublish);

    const handleModuleChange = (onChange: (value: string) => void) => (event: DropDownListChangeEvent) => {
        const formValues = getValues();
        const prevModule = formValues.module;

        if (
            prevModule === Constants.Modules.CM ||
            prevModule === Constants.Modules.CM2 ||
            prevModule === Constants.Modules.RAM
        ) {
            moduleSpecificCheckboxesState.set(prevModule, {
                includeReviews: formValues.includeReviews,
                includeInternalRisks: formValues.includeInternalRisks,
                includeNotApplicableRisks: formValues.includeNotApplicableRisks,
                includeComparisonDataset: formValues.includeComparisonDataset,
            });
        }

        const selectedModule = event.target.value;
        onChange(selectedModule);

        const isPlaceholderSelected =
            selectedModule !== Constants.Modules.CM &&
            selectedModule !== Constants.Modules.CM2 &&
            selectedModule !== Constants.Modules.RAM;
        if (isPlaceholderSelected) return;

        const prevModuleState = moduleSpecificCheckboxesState.value.get(selectedModule);

        if (!prevModuleState) return;
        const isCMSelected = selectedModule === Constants.Modules.CM || selectedModule === Constants.Modules.CM2;

        setValue('includeReviews', isCMSelected ? false : prevModuleState.includeReviews);
        setValue('includeNotApplicableRisks', isCMSelected ? false : prevModuleState.includeNotApplicableRisks);
        setValue('includeInternalRisks', isCMSelected ? false : prevModuleState.includeInternalRisks);
        setValue('includeComparisonDataset', isCMSelected ? prevModuleState.includeComparisonDataset : false);
    };

    const validateName = (name: string) => {
        const reportTemplatesNamesWithoutCurrent = reportTemplatesNames.filter(
            (tempName) => tempName !== currentReportTemplate?.templateName,
        );
        const isNameAlreadyExists = reportTemplatesNamesWithoutCurrent.includes(name.trim());
        if (isNameAlreadyExists) return t('admin:templateNameAlreadyExists');
    };

    const onSubmit = (isPublish?: boolean) => {
        const submitData = getValues();
        const isModuleRAM = submitData.module === Constants.Modules.RAM;
        const fullSubmitData: OpraReportTemplate = {
            ...submitData,
            orgKey: Constants.ObjectId.Empty, // On System Admin Level should be default id; On Organization Level should be organizationKey variable
            templateName: submitData.templateName.trim(),
            status: isNumber(submitData.status) ? submitData.status : undefined,
            module: isNumber(submitData.module) ? submitData.module : undefined,
            format: isNumber(submitData.format) ? submitData.format : undefined,
            // prevent module specific checks sent as checked
            includeReviews: isModuleRAM ? submitData.includeReviews : false,
            includeNotApplicableRisks: isModuleRAM ? submitData.includeNotApplicableRisks : false,
            includeInternalRisks: isModuleRAM ? submitData.includeInternalRisks : false,
            includeComparisonDataset: isModuleRAM ? false : submitData.includeComparisonDataset,
            ...(currentReportTemplate
                ? {
                      id: currentReportTemplate.id,
                      pbiDatasetId: currentReportTemplate.pbiDatasetId,
                      retirementDate: isString(submitData.retirementDate)
                          ? submitData.retirementDate
                          : submitData.retirementDate
                            ? moment(submitData.retirementDate).utcOffset(0, true).format('YYYY-MM-DD[T]HH:mm:ss[Z]')
                            : null,
                  }
                : {}),
            ...(isPublish
                ? {
                      publishReport: isPublish,
                  }
                : {}),
        };

        upsertReportTemplate(fullSubmitData)
            .then(() => {
                if (currentReportTemplate) {
                    const updateMessageKey = 'admin:reportTemplateSuccessfulUpdate';
                    NotificationService.showNotification(t(updateMessageKey, { id: currentReportTemplate.recordId }));
                } else {
                    const createMessageKey = 'admin:reportTemplateSuccessfulCreate';
                    NotificationService.showNotification(t(createMessageKey));
                }
                onClose(true);
            })
            .catch(() => {
                NotificationService.showNotification(t('errorMessage'));
            });
    };

    useEffect(() => {
        if (!currentReportTemplate?.module) return;
        moduleSpecificCheckboxesState.set(currentReportTemplate.module, {
            includeReviews: !!currentReportTemplate.includeReviews,
            includeInternalRisks: !!currentReportTemplate.includeInternalRisks,
            includeNotApplicableRisks: !!currentReportTemplate.includeNotApplicableRisks,
            includeComparisonDataset: !!currentReportTemplate.includeComparisonDataset,
        });
    }, []);

    return (
        <CustomDialog fullWidth maxWidth="md" open data-testid="report-template-dialog" className={className}>
            {isLoadingData && <Loading float />}
            <form id="report-template-form" className="report-template-form">
                <DialogTitle id="add-report-template-title">
                    {t(currentReportTemplate ? 'admin:editOPRAReportTemplate' : 'admin:addOPRAReportTemplate')}
                    <FontAwesomeIcon
                        icon={['fal', 'times']}
                        className="fa-lg close-icon-cv"
                        onClick={() => {
                            onClose();
                        }}
                        data-testid="close-dialog"
                    />
                </DialogTitle>
                <DialogContent>
                    <div className="type-edc-wrapper !k-mr-5 !k-mb-5">
                        <div className="form-control-type mr k-display-flex k-align-items-center">
                            <p className="text-wrap" data-testid="status-container">
                                <p className="title">{t('ra:status')}:</p>{' '}
                                {currentReportTemplate ? currentReportTemplate.reportStatusName : t('common:draft')}
                            </p>
                        </div>
                        <div className="form-control-type">
                            {isPublishedStatus && (
                                <Controller
                                    control={control}
                                    name="retirementDate"
                                    rules={{
                                        validate: {
                                            validDate: (value) => {
                                                const isEarlier =
                                                    !!value &&
                                                    unixDateFromUtc(value!, false, true)! <
                                                        unixDateFromUtc(
                                                            javascriptDateWithTimeFromUTC(new Date())!,
                                                            false,
                                                            true,
                                                        )!;
                                                if (isEarlier || !validateDate(value!)) {
                                                    return t('dateInvalid');
                                                }
                                            },
                                        },
                                    }}
                                    render={({ field: { onChange, value }, fieldState: { error } }) => (
                                        <div className="form-action-type">
                                            <DateTimePicker
                                                label={`${t('admin:retirementDate')}`}
                                                error={error?.message}
                                                customId="retirement-date"
                                                selectedDate={value || ''}
                                                handleDateChange={(v: DateTimePickerChangeEvent) => {
                                                    const dateValue = v?.value || null;
                                                    onChange(dateValue);
                                                }}
                                                min={javascriptDateWithTimeFromUTC(new Date())}
                                            />
                                        </div>
                                    )}
                                />
                            )}
                        </div>
                    </div>

                    <div className={cn('grid-cols-2 type-edc-wrapper !k-mb-4', { '!k-mb-9': errors.templateName })}>
                        <div className="form-control-type mr">
                            <Controller
                                control={control}
                                rules={{
                                    required: {
                                        value: true,
                                        message: t('ra:requiredFieldError'),
                                    },
                                }}
                                name="pbiReportId"
                                render={({ field: { onChange, value }, fieldState: { error } }) => (
                                    <SelectWithCategories
                                        customClassName="form-control-type"
                                        fieldSelect="pbiReportId"
                                        showLabels={false}
                                        searchable
                                        onChange={() => (event: DropDownListChangeEvent) =>
                                            onChange(event.target.value)
                                        }
                                        label={`${t('admin:powerBITemplate')}*`}
                                        labelField="pbiReportId"
                                        dataArray={powerBITemplateOptions}
                                        customId="power-bi-template-select"
                                        currentState={value}
                                        disabled={isInputsDisabled}
                                        error={error?.message}
                                    />
                                )}
                            />
                        </div>
                        <Controller
                            control={control}
                            name="templateName"
                            rules={{
                                required: {
                                    value: true,
                                    message: t('ra:requiredFieldError'),
                                },
                                validate: {
                                    maxLength: (value) => validateFieldLength(value, 'templateName', 200)!,
                                    existName: (value) => validateName(value),
                                },
                            }}
                            render={({ field: { onChange, value }, fieldState: { error } }) => (
                                <div className="form-control-type">
                                    <TextField
                                        className="max-h-42 !k-mb-6.5"
                                        label={`${t('admin:templateName')}*`}
                                        id="report-template-name"
                                        fullWidth
                                        value={value}
                                        onChange={onChange}
                                        data-testId="report-template-name"
                                        placeholder={t('admin:enterTemplateName')}
                                        helperText={
                                            <CharactersRemaining
                                                id="report-template-name-characters"
                                                length={value?.length}
                                                max={200}
                                                error={error?.message}
                                            />
                                        }
                                        variant="outlined"
                                        color="secondary"
                                        disabled={isInputsDisabled}
                                        error={!!error?.message}
                                    />
                                </div>
                            )}
                        />
                    </div>
                    <div className={cn('type-edc-wrapper !k-mb-3', { '!k-mb-8': hasErrorInFormatOrModule })}>
                        <Controller
                            control={control}
                            rules={{
                                required: {
                                    value: true,
                                    message: t('ra:requiredFieldError'),
                                },
                            }}
                            name="format"
                            render={({ field: { onChange, value }, fieldState: { error } }) => (
                                <SelectWithCategories
                                    customClassName="form-control-type mr"
                                    fieldSelect="format"
                                    onChange={() => (event: DropDownListChangeEvent) => onChange(event.target.value)}
                                    showLabels={false}
                                    label={`${t('common:format')}*`}
                                    labelField="format"
                                    dataArray={formatTypeOptions}
                                    customId="format-report-template-input"
                                    currentState={value}
                                    disabled={isInputsDisabled}
                                    error={error?.message}
                                />
                            )}
                        />
                        <Controller
                            control={control}
                            rules={{
                                required: {
                                    value: true,
                                    message: t('ra:requiredFieldError'),
                                },
                            }}
                            name="module"
                            render={({ field: { onChange, value }, fieldState: { error } }) => (
                                <SelectWithCategories
                                    customClassName="form-control-type"
                                    fieldSelect="module"
                                    showLabels={false}
                                    onChange={() => handleModuleChange(onChange)}
                                    label={`${t('common:module')}*`}
                                    labelField="module"
                                    dataArray={moduleTypeOptions}
                                    customId="module-report-template-input"
                                    currentState={value}
                                    disabled={isInputsDisabled}
                                    error={error?.message}
                                />
                            )}
                        />
                    </div>
                    <div className={cn('type-edc-wrapper !k-mb-0.5', { '!k-mb-1.5': !!moduleFieldValue })}>
                        <Controller
                            control={control}
                            name="includeSignatures"
                            render={({ field: { onChange, value } }) => (
                                <div className="checkboxes-wrapper !k-mr-5">
                                    <div className="left-part">
                                        <Checkbox
                                            rounded="none"
                                            id="includeSignatures-checkbox"
                                            data-testid="includeSignatures-checkbox"
                                            className="cell-checkbox no-border"
                                            checked={value}
                                            onChange={onChange}
                                            disabled={isInputsDisabled}
                                            label={t('admin:includeSignatories')}
                                            labelClassName={cn({ 'checkbox-label-disabled': isInputsDisabled })}
                                            size="medium"
                                        />
                                    </div>
                                </div>
                            )}
                        />
                        <Controller
                            control={control}
                            name="includeNameAndVersion"
                            render={({ field: { onChange, value } }) => (
                                <div className="checkboxes-wrapper">
                                    <div className="left-part">
                                        <Checkbox
                                            rounded="none"
                                            id="includeNameAndVersion-checkbox"
                                            data-testid="includeNameAndVersion-checkbox"
                                            className="cell-checkbox no-border"
                                            checked={value}
                                            onChange={onChange}
                                            disabled={isInputsDisabled}
                                            label={t('admin:includeNameVersion')}
                                            labelClassName={cn({ 'checkbox-label-disabled': isInputsDisabled })}
                                            size="medium"
                                        />
                                    </div>
                                </div>
                            )}
                        />
                    </div>
                    {Number(moduleFieldValue) === Constants.Modules.RAM && (
                        <div className="type-edc-wrapper !k-mb-1.5">
                            <Controller
                                control={control}
                                name="includeReviews"
                                render={({ field: { onChange, value } }) => (
                                    <div className="checkboxes-wrapper !k-mr-5">
                                        <div className="left-part">
                                            <Checkbox
                                                rounded="none"
                                                id="includeReviews-checkbox"
                                                data-testid="includeReviews-checkbox"
                                                className="cell-checkbox no-border"
                                                checked={value}
                                                onChange={onChange}
                                                disabled={isInputsDisabled}
                                                label={t('admin:includeReview')}
                                                labelClassName={cn({ 'checkbox-label-disabled': isInputsDisabled })}
                                                size="medium"
                                            />
                                        </div>
                                    </div>
                                )}
                            />
                            <Controller
                                control={control}
                                name="includeNotApplicableRisks"
                                render={({ field: { onChange, value } }) => (
                                    <div className="checkboxes-wrapper">
                                        <div className="left-part">
                                            <Checkbox
                                                rounded="none"
                                                id="includeNotApplicableRisks-checkbox"
                                                data-testid="includeNotApplicableRisks-checkbox"
                                                className="cell-checkbox no-border"
                                                checked={value}
                                                onChange={onChange}
                                                disabled={isInputsDisabled}
                                                label={t('admin:includeNotApplicableRisks')}
                                                labelClassName={cn({ 'checkbox-label-disabled': isInputsDisabled })}
                                                size="medium"
                                            />
                                        </div>
                                    </div>
                                )}
                            />
                        </div>
                    )}
                    <div className="type-edc-wrapper !k-mb-3">
                        {Number(moduleFieldValue) === Constants.Modules.RAM && (
                            <Controller
                                control={control}
                                name="includeInternalRisks"
                                render={({ field: { onChange, value } }) => (
                                    <div className="checkboxes-wrapper">
                                        <div className="left-part">
                                            <Checkbox
                                                rounded="none"
                                                id="includeInternalRisks-checkbox"
                                                data-testid="includeInternalRisks-checkbox"
                                                className="cell-checkbox no-border"
                                                checked={value}
                                                onChange={onChange}
                                                disabled={isInputsDisabled}
                                                label={t('admin:includeInternalRisks')}
                                                labelClassName={cn({ 'checkbox-label-disabled': isInputsDisabled })}
                                                size="medium"
                                            />
                                        </div>
                                    </div>
                                )}
                            />
                        )}
                        {(moduleFieldValue === Constants.Modules.CM ||
                            moduleFieldValue === Constants.Modules.CM2 ||
                            moduleFieldValue === Constants.Modules.SM) && (
                            <Controller
                                control={control}
                                name="includeComparisonDataset"
                                render={({ field: { onChange, value } }) => (
                                    <div className="checkboxes-wrapper">
                                        <div className="left-part">
                                            <Checkbox
                                                rounded={null}
                                                id="includeComparisonDataset-checkbox"
                                                data-testid="includeComparisonDataset-checkbox"
                                                className="cell-checkbox no-border"
                                                checked={value}
                                                onChange={onChange}
                                                disabled={isInputsDisabled}
                                                label={t('admin:includeComparisonDataset')}
                                                labelClassName={cn({ 'checkbox-label-disabled': isInputsDisabled })}
                                                size="medium"
                                            />
                                        </div>
                                    </div>
                                )}
                            />
                        )}
                    </div>
                    <div className="type-edc-wrapper !k-mb-1 !k-mt-2.5">
                        <Controller
                            control={control}
                            name="description"
                            rules={{
                                validate: {
                                    maxLength: (value) => validateFieldLength(value, 'description', 500)!,
                                },
                            }}
                            render={({ field: { onChange, value }, fieldState: { error } }) => (
                                <TextField
                                    label={`${t('description')}`}
                                    id="report-template-description"
                                    data-testId="report-template-description"
                                    multiline
                                    minRows={5}
                                    fullWidth
                                    value={value}
                                    onChange={onChange}
                                    placeholder={t('admin:enterDescription')}
                                    helperText={
                                        <CharactersRemaining
                                            id="report-template-description-сharacters"
                                            length={value?.length}
                                            max={500}
                                            error={error?.message}
                                        />
                                    }
                                    variant="outlined"
                                    color="secondary"
                                    disabled={isRetiredStatus}
                                    error={!!error?.message}
                                    className="before-select"
                                />
                            )}
                        />
                    </div>
                </DialogContent>
                <DialogActions
                    className={cn('!k-px-5 k-mb-6', {
                        'k-flex !k-justify-content-between': currentReportTemplate,
                    })}
                >
                    {currentReportTemplate && <ModalUpdatedInfo item={currentReportTemplate} isBFF />}
                    <div>
                        {!isPublishedStatus && !isRetiredStatus && !dateWithinLastHour && (
                            <Button
                                className="mr"
                                disabled={!isValid || isMutating}
                                text={currentReportTemplate ? t('admin:updateAndPublish') : t('admin:addAndPublish')}
                                data-testid="submit-publish-report-template"
                                id="submit-publish-report-template"
                                type="button"
                                onClick={handleSubmit(() => {
                                    if (confirmActionFn) {
                                        confirmActionFn(
                                            () => onSubmit(true),
                                            Constants.Reporting.reportingDialogConfirmation.publish,
                                        );
                                    } else {
                                        onClose(false, true);
                                    }
                                })}
                            />
                        )}
                        <Button
                            disabled={!isValid || isMutating || !isDirty}
                            text={currentReportTemplate ? 'update' : 'add'}
                            id="submit-report-template"
                            data-testid="submit-report-template"
                            type="button"
                            onClick={handleSubmit(
                                retirementDateFieldValue &&
                                    retirementDateFieldValue !== currentReportTemplate?.retirementDate &&
                                    confirmActionFn
                                    ? () => {
                                          confirmActionFn(
                                              onSubmit,
                                              Constants.Reporting.reportingDialogConfirmation.retire,
                                          );
                                      }
                                    : () => onSubmit(),
                            )}
                        />
                    </div>
                </DialogActions>
            </form>
        </CustomDialog>
    );
};

export default AddReportTemplateDialog;
